/* @flow */
import * as React from 'react';

import Menu from 'components/Menu';
import Glyph from 'components/Glyph';
import Button from 'components/Button';
import MenuItem from 'components/MenuItem';
import MenuButton from 'components/MenuButton';
import Text from 'components/Text';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Tooltip from 'components/Tooltip';
import LayerTrigger from 'components/LayerTrigger';

type AdminIntegrationProps = {
  onActionUnauthorized: Function,
  organizationUUID: string,
  canEdit: boolean,
  integration: any,
  isToggleable?: boolean,
  isEnabled?: boolean,
  toggleClickHandler?: void => void,
};

const Enabled = () => <span className="AdminIntegratedToolsView-enabled">Enabled</span>;

/* $FlowFixMe */
const ToggleButton = ({ isEnabled, canEdit, ...props }: { isEnabled: boolean, canEdit: boolean }) => (
  <Button
    size="xsmall"
    level={isEnabled ? 'secondary' : 'primary'}
    disabled={!canEdit}
    {...props}
  >
    {isEnabled ? 'Disable' : 'Enable'}
  </Button>
);

const DistrictOverridenToggleButton = props => (
  <LayerTrigger layer={<Tooltip>Enabled by your district</Tooltip>} delay={50} placement="top">
    <div>
      <ToggleButton isEnabled {...props} />
    </div>
  </LayerTrigger>
);

const AdminIntegration = (props: AdminIntegrationProps) => {
  const { canEdit, isEnabled, isToggleable, toggleClickHandler } = props;
  const { isACIIntegrated, providerKey } = props.integration;

  const isLTIIntegrated = props.integration.LTI != null;
  const ACIRoute = `/install_aci/${props.organizationUUID}/${props.integration.providerKey}`;
  const LTIRoute = `/install_lti/${props.organizationUUID}/${props.integration.providerKey}`;

  const actions = [];

  const ACIButtonProps = canEdit ? { to: ACIRoute } : { onClick: props.onActionUnauthorized };
  const LTIButtonProps = canEdit ? { to: LTIRoute } : { onClick: props.onActionUnauthorized };

  if (isToggleable) {
    actions.push(canEdit
      ? <ToggleButton isEnabled={isEnabled} canEdit onClick={toggleClickHandler} key={`admin-integration-${providerKey}`} />
      : <DistrictOverridenToggleButton key={`admin-integration-${providerKey}`} />
    );
  } else if (!isACIIntegrated) {
    actions.push(
      <Button key="aci" size="xsmall" level="primary" {...ACIButtonProps}>
        Set up
      </Button>
    );
  } else if (!isLTIIntegrated) {
    actions.push(
      <Button key="lti" size="xsmall" level={'primary'} {...LTIButtonProps}>
        Set up LTI
      </Button>
    );
  }

  if (isACIIntegrated || isLTIIntegrated) {
    actions.push(
      <MenuButton
        key="menu"
        size="xsmall"
        level="secondary"
        alignment="right"
        placement="bottom"
        menu={
          <Menu>
            {isACIIntegrated && <MenuItem {...ACIButtonProps}>Manage app</MenuItem>}
            {isLTIIntegrated && <MenuItem {...LTIButtonProps}>Manage LTI</MenuItem>}
          </Menu>
        }>
        <Glyph name="ellipses" solo />
      </MenuButton>
    );
  }

  return (
    <div className="AdminIntegratedToolsView-admin">
      <div className="AdminIntegratedToolsView-admin-info">
        <MediaObject gutter="medium" center>
          <div className="AdminIntegratedToolsView-admin-info-logo">
            <Image src={props.integration.iconURL} />
          </div>
          <div>
            <div>
              <Text weight="semibold" size="medium">
                {props.integration.title}
              </Text>
              {(isEnabled || isACIIntegrated) ? <Enabled /> : null}
            </div>
            <Text element="div" color="thunder">
              {props.integration.productShortDescription}
            </Text>
          </div>
        </MediaObject>
      </div>
      <div className="AdminIntegratedToolsView-admin-buttons">{actions}</div>
    </div>
  );
};

export default AdminIntegration;
